/**
 * Workplan — auto-generated from license graph.
 * From FRONTEND_BUILD_SPEC §3.4: Phases, streams, parallel brackets, critical path.
 */
import { useState } from "react";
import { CheckCircle, Circle, AlertTriangle, Clock } from "lucide-react";
import { AIInsight } from "../shared/AIInsight.tsx";

interface License {
  license_type_id: string;
  license_name: string;
  issuing_authority: string;
  status: string;
  statutory_timeline_days: number | null;
  prerequisite_license_ids: string;
  fields_total: number;
  fields_complete: number;
  docs_total: number;
  docs_complete: number;
}

interface WorkplanViewProps {
  licenses: License[];
  projectId: string;
  countryCode: string;
}

interface Phase {
  label: string;
  description: string;
  monthRange: string;
  streams: Stream[];
  unlockText?: string;
}

interface Stream {
  label: string;
  isCriticalPath: boolean;
  tasks: Task[];
}

interface PrereqInfo {
  licenseId: string;
  name: string;
  status: string;
  phaseLabel: string;
}

interface Task {
  licenseId: string;
  name: string;
  authority: string;
  status: string;
  durationMonths: number;
  isBlocked: boolean;
  blockedByPrereqs: PrereqInfo[];
  prereqsMet: number;
  prereqsTotal: number;
  isLongTimeline: boolean;
  fieldsComplete: number;
  fieldsTotal: number;
  docsComplete: number;
  docsTotal: number;
}

export function WorkplanView({ licenses, projectId, countryCode }: WorkplanViewProps) {
  const phases = generatePhases(licenses, countryCode);

  if (licenses.length === 0) {
    return <p className="text-[16px] text-text-secondary py-4">No licenses to generate workplan from.</p>;
  }

  const totalTasks = phases.reduce((s, p) => s + p.streams.reduce((ss, st) => ss + st.tasks.length, 0), 0);
  const completedTasks = phases.reduce((s, p) => s + p.streams.reduce((ss, st) =>
    ss + st.tasks.filter(t => t.status === "approved").length, 0), 0);

  return (
    <div className="space-y-1">
      {/* Summary */}
      <div className="flex items-center gap-4 mb-4 text-[14px] text-text-secondary">
        <span>{completedTasks}/{totalTasks} tasks complete</span>
        <span>·</span>
        <span>{phases.length} phases</span>
      </div>

      {/* AI insight */}
      <div className="mb-4">
        <AIInsight
          text={getCriticalPathInsight(phases)}
        />
      </div>

      {/* Phases */}
      {phases.map((phase, pi) => (
        <div key={phase.label}>
          {/* Phase header */}
          <div className="flex items-center gap-2 mb-3 mt-2">
            <h3 className="text-[13px] font-medium text-text-tertiary uppercase tracking-wider">
              {phase.label}
            </h3>
            <span className="text-[12px] text-text-tertiary">{phase.monthRange}</span>
            {phase.description && (
              <span className="text-[12px] text-text-tertiary ml-auto">{phase.description}</span>
            )}
          </div>

          {/* Streams — fork/merge tree when multiple streams or any stream has 2+ tasks */}
          {phase.streams.length > 1 || phase.streams.some(s => s.tasks.length > 1) ? (
            <div className="ml-4 mb-3">
              {/* Streams with branch connectors */}
              <div className="border-l-2 border-border-secondary">
                {phase.streams.map((stream) => (
                  <div key={stream.label} className="py-1">
                    {/* Critical path badge (only if flagged) */}
                    {stream.isCriticalPath && (
                      <div className="ml-4 mb-1">
                        <span className="text-[11px] font-medium px-1.5 py-0.5 bg-danger-light text-danger rounded-full">
                          Critical path
                        </span>
                      </div>
                    )}
                    {/* Task cards with connectors */}
                    <div className="space-y-1">
                      {stream.tasks.map(task => (
                        <div key={task.licenseId} className="relative pl-4">
                          {/* Horizontal connector to card border */}
                          <div className="absolute left-0 top-1/2 w-4 border-t-2 border-border-secondary" />
                          {/* Dot on the trunk line */}
                          <div className="absolute left-[-5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-border-secondary border-2 border-surface" />
                          <TaskRow task={task} projectId={projectId} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Unlock text — terminates the branch line */}
              {phase.unlockText && (
                <div className="relative pl-4 pt-2 pb-1">
                  <div className="absolute left-0 top-0 h-1/2 border-l-2 border-border-secondary" />
                  <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-text-tertiary" />
                  <span className="ml-2 text-[12px] text-text-tertiary">{phase.unlockText}</span>
                </div>
              )}
            </div>
          ) : (
            phase.streams.map(stream => (
              <div key={stream.label} className="mb-2">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[13px] font-medium text-text-secondary">{stream.label}</span>
                  {stream.isCriticalPath && (
                    <span className="text-[11px] font-medium px-1.5 py-0.5 bg-danger-light text-danger rounded-full">
                      Critical path
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  {stream.tasks.map(task => (
                    <TaskRow key={task.licenseId} task={task} projectId={projectId} />
                  ))}
                </div>
              </div>
            ))
          )}

          {/* Unlock text for truly single-task phases */}
          {!(phase.streams.length > 1 || phase.streams.some(s => s.tasks.length > 1)) && phase.unlockText && (
            <div className="relative py-2 pl-4">
              <div className="absolute left-0 top-0 h-1/2 border-l-2 border-border-secondary" />
              <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-text-tertiary" />
              <span className="ml-2 text-[12px] text-text-tertiary">{phase.unlockText}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


function TaskRow({ task, projectId }: { task: Task; projectId: string }) {
  const statusIcon = task.status === "approved" ? (
    <CheckCircle size={14} className="text-brand" />
  ) : task.isBlocked ? (
    <AlertTriangle size={14} className="text-danger" />
  ) : task.status === "preparing" || task.status === "submitted" ? (
    <Clock size={14} className="text-warning" />
  ) : (
    <Circle size={14} className="text-text-tertiary" />
  );

  const blockerSummary = task.blockedByPrereqs.length <= 2
    ? task.blockedByPrereqs.map(p => `${p.name}${p.phaseLabel ? ` (${p.phaseLabel})` : ""}`).join(", ")
    : `${task.blockedByPrereqs.slice(0, 2).map(p => `${p.name}${p.phaseLabel ? ` (${p.phaseLabel})` : ""}`).join(", ")} + ${task.blockedByPrereqs.length - 2} more`;

  return (
    <a
      href={`/projects/${projectId}/licenses/${task.licenseId}`}
      className={`flex items-center gap-2.5 px-3 py-2 bg-surface border border-border rounded-xl card-interactive hover:border-border-secondary no-underline text-text-primary ${
        task.isBlocked ? "opacity-50" : ""
      }`}
    >
      {statusIcon}
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium truncate">{task.name}</div>
        <div className="text-[12px] text-text-tertiary">
          {task.authority} · ~{task.durationMonths}mo
          {task.isLongTimeline && (
            <span className="inline-flex items-center gap-0.5 ml-2 text-[11px] font-medium text-warning">
              <Clock size={11} />
              Long timeline
            </span>
          )}
          {(task.fieldsTotal > 0 || task.docsTotal > 0) && (
            <span className="ml-2">
              {task.fieldsTotal > 0 && <span>{task.fieldsComplete}/{task.fieldsTotal} fields</span>}
              {task.fieldsTotal > 0 && task.docsTotal > 0 && <span> · </span>}
              {task.docsTotal > 0 && <span>{task.docsComplete}/{task.docsTotal} docs</span>}
            </span>
          )}
        </div>
      </div>
      {task.isBlocked && (
        <div className="text-right shrink-0 max-w-[220px]">
          <span className="text-[12px] text-danger font-medium">Blocked</span>
          {task.prereqsTotal > 1 && (
            <div className="text-[11px] text-text-tertiary">{task.prereqsMet}/{task.prereqsTotal} prerequisites met</div>
          )}
          <div className="text-[11px] text-text-tertiary truncate" title={blockerSummary}>
            Waiting on: {blockerSummary}
          </div>
        </div>
      )}
    </a>
  );
}

// ─── Workplan generation logic ───

function generatePhases(licenses: License[], countryCode: string): Phase[] {
  const parsed = licenses.map(l => ({
    ...l,
    prereqs: tryParseJSON(l.prerequisite_license_ids, []) as string[],
    durationMonths: l.statutory_timeline_days ? Math.max(1, Math.round(l.statutory_timeline_days / 30)) : 3,
  }));

  const licMap = new Map(parsed.map(l => [l.license_type_id, l]));

  // Phase definitions per country
  const phaseConfig: Record<string, Array<{ label: string; ids: string[]; desc: string; unlock?: string }>> = {
    KE: [
      { label: "Phase 1: Pre-development", ids: ["KE-L04", "KE-L11"], desc: "Secure land + company", unlock: "Land control established — unlocks Phase 2" },
      { label: "Phase 2: Environmental + Grid", ids: ["KE-L02", "KE-L03", "KE-L05", "KE-L04B"], desc: "Parallel regulatory streams", unlock: "EIA + grid study complete — unlocks Phase 3" },
      { label: "Phase 3: Licensing + Equipment", ids: ["KE-L01", "KE-L06", "KE-L07", "KE-L08", "KE-L09"], desc: "Core license applications", unlock: "Licenses granted — unlocks Phase 4" },
      { label: "Phase 4: Construction + COD", ids: ["KE-L10", "KE-L12"], desc: "Build and commission" },
    ],
    NG: [
      { label: "Phase 1: Pre-development", ids: ["NG-L05", "NG-L11"], desc: "Land + registration", unlock: "Land secured — unlocks Phase 2" },
      { label: "Phase 2: Environmental + Grid", ids: ["NG-L03", "NG-L08"], desc: "EIA + grid connection", unlock: "EIA + evacuation complete — unlocks Phase 3" },
      { label: "Phase 3: Licensing + Equipment", ids: ["NG-L01", "NG-L01B", "NG-L02", "NG-L02B", "NG-L04", "NG-L06", "NG-L10"], desc: "NERC licensing", unlock: "Licensed — unlocks Phase 4" },
      { label: "Phase 4: Construction + COD", ids: ["NG-L07", "NG-L09", "NG-L12"], desc: "Build and commission" },
    ],
    ZA: [
      { label: "Phase 1: Pre-development", ids: ["ZA-L04", "ZA-L04B", "ZA-L10"], desc: "Land + rezoning", unlock: "Land rights established — unlocks Phase 2" },
      { label: "Phase 2: Environmental + Grid + Heritage", ids: ["ZA-L02", "ZA-L02B", "ZA-L03", "ZA-L05", "ZA-L06", "ZA-L07"], desc: "Parallel environmental streams", unlock: "EA + grid access — unlocks Phase 3" },
      { label: "Phase 3: Registration + Licensing", ids: ["ZA-L01", "ZA-L01B", "ZA-L11"], desc: "NERSA registration", unlock: "Registered — unlocks Phase 4" },
      { label: "Phase 4: Construction + COD", ids: ["ZA-L08", "ZA-L09"], desc: "Build and commission" },
    ],
    GH: [
      { label: "Phase 1: Pre-development", ids: ["GH-L04", "GH-L07", "GH-L09"], desc: "Land + business permit + tax exemption", unlock: "Land secured — unlocks Phase 2" },
      { label: "Phase 2: Environmental + Grid", ids: ["GH-L02", "GH-L03", "GH-L11", "GH-L12"], desc: "EPA + GRIDCo + utilities", unlock: "EPA permit + grid access — unlocks Phase 3" },
      { label: "Phase 3: Licensing + Equipment", ids: ["GH-L01", "GH-L05", "GH-L06", "GH-L08", "GH-L10"], desc: "Energy Commission + PURC + building", unlock: "Licensed — unlocks Phase 4" },
      { label: "Phase 4: Construction + COD", ids: [], desc: "Build and commission" },
    ],
    NA: [
      { label: "Phase 1: Pre-development", ids: ["NA-L04", "NA-L05"], desc: "Planning consent + land", unlock: "Land secured — unlocks Phase 2" },
      { label: "Phase 2: Environmental + Grid", ids: ["NA-L02", "NA-L03"], desc: "ECC + NamPower", unlock: "Clearance + grid — unlocks Phase 3" },
      { label: "Phase 3: Licensing + Equipment", ids: ["NA-L01", "NA-L06", "NA-L07", "NA-L08"], desc: "ECB licence + building", unlock: "Licensed — unlocks Phase 4" },
      { label: "Phase 4: Construction + COD", ids: [], desc: "Build and commission" },
    ],
    ZM: [
      { label: "Phase 1: Pre-development", ids: ["ZM-L04", "ZM-L05"], desc: "Land leasehold + community consent", unlock: "Land secured — unlocks Phase 2" },
      { label: "Phase 2: Environmental + Grid", ids: ["ZM-L02", "ZM-L03"], desc: "ZEMA + ZESCO", unlock: "EIA + grid — unlocks Phase 3" },
      { label: "Phase 3: Licensing + Equipment", ids: ["ZM-L01", "ZM-L06", "ZM-L07", "ZM-L08"], desc: "ERB licence + building", unlock: "Licensed — unlocks Phase 4" },
      { label: "Phase 4: Construction + COD", ids: [], desc: "Build and commission" },
    ],
  };

  // Build license-to-phase-label map for prerequisite display
  const activePConfig = phaseConfig[countryCode] ?? phaseConfig.KE!;
  const licenseToPhaseLabel = new Map<string, string>();
  for (const pc of activePConfig) {
    const shortLabel = pc.label.split(":")[0]!; // "Phase 1"
    for (const id of pc.ids) {
      licenseToPhaseLabel.set(id, shortLabel);
    }
  }

  // Critical path: EIA and grid are typically longest
  const criticalIds = ["KE-L02", "KE-L03", "NG-L03", "NG-L08", "ZA-L02", "ZA-L06", "GH-L02", "GH-L03", "NA-L02", "NA-L03", "ZM-L02", "ZM-L03"];

  const phases = activePConfig.map(pc => {
    const phaseLicenses = parsed.filter(l => pc.ids.includes(l.license_type_id));
    if (phaseLicenses.length === 0) return null;

    // Group into streams by related licenses
    const streams: Stream[] = [];
    const used = new Set<string>();

    for (const lic of phaseLicenses) {
      if (used.has(lic.license_type_id)) continue;
      used.add(lic.license_type_id);

      const isCritical = criticalIds.includes(lic.license_type_id);

      // Find related licenses in same phase (share prerequisites)
      const related = phaseLicenses.filter(l =>
        !used.has(l.license_type_id) &&
        l.prereqs.some(p => lic.prereqs.includes(p))
      );

      const streamLicenses = [lic, ...related];
      related.forEach(r => used.add(r.license_type_id));

      streams.push({
        label: streamLicenses.length === 1
          ? lic.license_name
          : `${lic.license_name}${related.length > 0 ? ` + ${related.length} related` : ""}`,
        isCriticalPath: isCritical
          || streamLicenses.some(l => criticalIds.includes(l.license_type_id))
          || streamLicenses.some(l => l.durationMonths > 12),
        tasks: streamLicenses.map(l => {
          const approvedPrereqs = l.prereqs.filter(preId => {
            const pre = licMap.get(preId);
            return pre && pre.status === "approved";
          });
          const unmetPrereqs: PrereqInfo[] = l.prereqs
            .filter(preId => {
              const pre = licMap.get(preId);
              return !pre || pre.status !== "approved";
            })
            .map(preId => {
              const pre = licMap.get(preId);
              return {
                licenseId: preId,
                name: pre?.license_name ?? preId,
                status: pre?.status ?? "unknown",
                phaseLabel: licenseToPhaseLabel.get(preId) ?? "",
              };
            });

          return {
            licenseId: l.license_type_id,
            name: l.license_name,
            authority: l.issuing_authority,
            status: l.status,
            durationMonths: l.durationMonths,
            isBlocked: unmetPrereqs.length > 0,
            blockedByPrereqs: unmetPrereqs,
            prereqsMet: approvedPrereqs.length,
            prereqsTotal: l.prereqs.length,
            isLongTimeline: l.durationMonths > 12,
            fieldsComplete: l.fields_complete || 0,
            fieldsTotal: l.fields_total || 0,
            docsComplete: l.docs_complete || 0,
            docsTotal: l.docs_total || 0,
          };
        }),
      });
    }

    const monthStart = pc === phaseConfig[countryCode]?.[0] ? 1 : undefined;

    return {
      label: pc.label,
      description: pc.desc,
      monthRange: "",
      streams,
      unlockText: pc.unlock,
    } satisfies Phase;
  }).filter(Boolean) as Phase[];

  return phases;
}

function getCriticalPathInsight(phases: Phase[]): string {
  const criticalStreams = phases.flatMap(p => p.streams.filter(s => s.isCriticalPath));
  const blockedCritical = criticalStreams.flatMap(s => s.tasks.filter(t => t.isBlocked));

  if (blockedCritical.length > 0) {
    return `${blockedCritical[0]!.name} is on the critical path but blocked. Unblock its prerequisites first to avoid timeline delays.`;
  }

  const incompleteCritical = criticalStreams.flatMap(s => s.tasks.filter(t => t.status !== "approved"));
  if (incompleteCritical.length > 0) {
    return `Focus on ${incompleteCritical[0]!.name} — it's on the critical path with ~${incompleteCritical[0]!.durationMonths} months processing time. Starting early here saves the most time overall.`;
  }

  return "All critical path items are on track. Continue with parallel streams to maintain your timeline.";
}

import { tryParseJSON } from "../../lib/utils.ts";
